import { useEffect, useRef, useState, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import type { CounterpartyDTO } from '@onyx/shared';
import { inTelegram, haptic } from '../lib/telegram';
import { back } from '../lib/router';
import { useT } from '../lib/i18n';
import type { NumberParts } from '../lib/format';

/* ---------- Money ---------- */

/** A sum on the stone: integer at full weight, the fraction and symbol quieter. */
export function Money({ parts, unit, className = '', size = 'md' }: { parts: NumberParts; unit?: string; className?: string; size?: 'sm' | 'md' | 'lg' | 'xl' | 'display' }) {
  const number = (
    <>
      {parts.sign}
      {parts.int}
      {parts.frac && (
        <span className="money__frac">
          {parts.sep}
          {parts.frac}
        </span>
      )}
    </>
  );
  return (
    <span className={`money money--${size} num ${className}`}>
      {parts.symbol && parts.symbolFirst && <span className="money__sym">{parts.symbol}</span>}
      {number}
      {parts.symbol && !parts.symbolFirst && <span className="money__sym money__sym--after">{parts.symbol}</span>}
      {unit && <span className="money__unit">{unit}</span>}
    </span>
  );
}

/* ---------- Buttons ---------- */

type SlabProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'primary' | 'quiet' | 'danger' | 'ghost';
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
};

/** The primary action is a slab cut from the opposite band of the stone. */
export function Slab({ tone = 'primary', loading, icon, block = true, children, className = '', onClick, ...rest }: SlabProps) {
  const t = useT();
  // Destructive actions never fire on one tap: the first tap arms, the second (within 4 s) runs.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(id);
  }, [armed]);
  return (
    <button
      type="button"
      {...rest}
      className={`slab slab--${tone}${block ? ' slab--block' : ''}${loading ? ' is-loading' : ''}${armed ? ' is-armed' : ''} ${className}`}
      aria-busy={loading || undefined}
      onClick={(e) => {
        if (loading) return;
        if (tone === 'danger' && !armed) {
          haptic.warn();
          setArmed(true);
          return;
        }
        setArmed(false);
        haptic.tap();
        onClick?.(e);
      }}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : icon}
      <span className="slab__label" aria-live={tone === 'danger' ? 'polite' : undefined}>
        {armed ? t['common.confirmAgain'] : children}
      </span>
    </button>
  );
}

export function IconButton({ label, children, onClick, className = '' }: { label: string; children: ReactNode; onClick?: () => void; className?: string }) {
  return (
    <button type="button" className={`icon-btn ${className}`} aria-label={label} title={label} onClick={onClick}>
      {children}
    </button>
  );
}

/* ---------- Screen chrome ---------- */

/** In Telegram the native BackButton handles back; in a browser we draw one. */
export function ScreenHeader({ title, onBack, right, sub }: { title: ReactNode; onBack?: (() => void) | false; right?: ReactNode; sub?: ReactNode }) {
  const t = useT();
  const showBack = !inTelegram && onBack !== false;
  // Tab roots (no back) carry a large left-aligned title; inner screens a compact centred one.
  if (onBack === false) {
    return (
      <header className="screen-header screen-header--large">
        <h1 className="screen-header__large">{title}</h1>
        {right && <span className="screen-header__right">{right}</span>}
      </header>
    );
  }
  return (
    <header className="screen-header">
      <div className="screen-header__bar">
        {showBack ? (
          <IconButton label={t['common.back']} onClick={onBack || (() => back())}>
            <CaretLeft size={22} weight="bold" />
          </IconButton>
        ) : (
          <span className="screen-header__spacer" />
        )}
        <h1 className="screen-header__title">{title}</h1>
        <span className="screen-header__right">{right}</span>
      </div>
      {sub && <div className="screen-header__sub">{sub}</div>}
    </header>
  );
}

export function Screen({ children, className = '', footer }: { children: ReactNode; className?: string; footer?: ReactNode }) {
  return (
    <main className={`screen ${className}`}>
      <div className="screen__body">{children}</div>
      {footer && <div className="screen__footer">{footer}</div>}
    </main>
  );
}

/* ---------- Rows and lists ---------- */

export function Row({
  leading,
  title,
  subtitle,
  trailing,
  trailingSub,
  onClick,
  chevron,
  className = '',
  ariaLabel,
  wrapSub,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  trailingSub?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Let a subtitle that carries a fact (e.g. funds in processing) wrap instead of truncating. */
  wrapSub?: boolean;
}) {
  const body = (
    <>
      {leading && <span className="row__lead">{leading}</span>}
      <span className="row__main">
        <span className="row__title">{title}</span>
        {subtitle && <span className={`row__sub${wrapSub ? ' row__sub--wrap' : ''}`}>{subtitle}</span>}
      </span>
      {(trailing || trailingSub) && (
        <span className="row__trail">
          {trailing && <span className="row__trail-main">{trailing}</span>}
          {trailingSub && <span className="row__trail-sub">{trailingSub}</span>}
        </span>
      )}
      {chevron && <CaretRight className="row__chev" size={16} weight="bold" aria-hidden="true" />}
    </>
  );
  return onClick ? (
    <button type="button" className={`row row--btn ${className}`} onClick={onClick} aria-label={ariaLabel}>
      {body}
    </button>
  ) : (
    <div className={`row ${className}`}>{body}</div>
  );
}

export function Section({ title, action, children, className = '' }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`section ${className}`}>
      {(title || action) && (
        <div className="section__head">
          {title && <h2 className="section__title">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Facts read back before an irreversible action, one per line. */
export function Facts({ items }: { items: Array<{ label: ReactNode; value: ReactNode; strong?: boolean } | false | null | undefined | '' | 0> }) {
  return (
    <dl className="facts">
      {items.filter(Boolean).map((it, i) => {
        const item = it as { label: ReactNode; value: ReactNode; strong?: boolean };
        return (
          <div key={i} className={`facts__row${item.strong ? ' facts__row--strong' : ''}`}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/* ---------- Inputs ---------- */

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
  aside,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
  aside?: ReactNode;
}) {
  return (
    <div className={`field${error ? ' has-error' : ''}`}>
      <div className="field__head">
        <label className="field__label" htmlFor={htmlFor}>
          {label}
        </label>
        {aside}
      </div>
      {children}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="field__hint">{hint}</p>
      )}
    </div>
  );
}

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`segmented__opt${o.value === value ? ' is-on' : ''}`}
          onClick={() => {
            haptic.select();
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- Sheet ---------- */

export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode }) {
  const t = useT();
  const [mounted, setMounted] = useState(open);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!mounted) return null;
  return createPortal(
    <div className={`sheet${open ? ' is-open' : ''}`} onTransitionEnd={() => !open && setMounted(false)}>
      <div className="sheet__scrim" onClick={onClose} />
      <div className="sheet__panel" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} tabIndex={-1} ref={panel}>
        <div className="sheet__grip" aria-hidden="true" />
        <div className="sheet__head">
          <h2 className="sheet__title">{title}</h2>
          <IconButton label={t['common.close']} onClick={onClose}>
            <X size={20} weight="bold" />
          </IconButton>
        </div>
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/* ---------- Feedback ---------- */

export function Skeleton({ w = '100%', h = 16, r = 8 }: { w?: number | string; h?: number; r?: number }) {
  return <span className="skeleton" style={{ width: w, height: h, borderRadius: r }} aria-hidden="true" />;
}

export function ErrorState({ text, onRetry }: { text: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="state state--error" role="alert">
      <p>{text}</p>
      {onRetry && (
        <Slab tone="quiet" block={false} onClick={onRetry}>
          {t['common.retry']}
        </Slab>
      )}
    </div>
  );
}

export function Notice({ tone = 'info', children, icon }: { tone?: 'info' | 'warn' | 'good' | 'bad'; children: ReactNode; icon?: ReactNode }) {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'bad' || tone === 'warn' ? 'note' : undefined}>
      {icon && <span className="notice__icon">{icon}</span>}
      <div className="notice__text">{children}</div>
    </div>
  );
}

export function Avatar({ user, size = 36 }: { user: Pick<CounterpartyDTO, 'firstName' | 'photoUrl'> | null; size?: number }) {
  const [broken, setBroken] = useState(false);
  const initial = (user?.firstName ?? '?').trim().charAt(0).toUpperCase();
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {user?.photoUrl && !broken ? <img src={user.photoUrl} alt="" onError={() => setBroken(true)} /> : initial}
    </span>
  );
}

/* ---------- Toast ---------- */

type ToastMsg = { id: number; text: string; tone: 'good' | 'bad' | 'info' };
let pushToast: ((t: Omit<ToastMsg, 'id'>) => void) | null = null;

export const toast = {
  good: (text: string) => pushToast?.({ text, tone: 'good' }),
  bad: (text: string) => pushToast?.({ text, tone: 'bad' }),
  info: (text: string) => pushToast?.({ text, tone: 'info' }),
};

export function ToastHost() {
  const [items, setItems] = useState<ToastMsg[]>([]);
  useEffect(() => {
    let id = 0;
    pushToast = (msg) => {
      const item = { ...msg, id: ++id };
      setItems((xs) => [...xs.slice(-2), item]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== item.id)), 2600);
    };
    return () => {
      pushToast = null;
    };
  }, []);
  return (
    <div className="toasts" aria-live="polite">
      {items.map((m) => (
        <div key={m.id} className={`toast toast--${m.tone}`}>
          {m.text}
        </div>
      ))}
    </div>
  );
}
